import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ActionIcon,
  Alert,
  Box,
  Center,
  Loader,
  Menu,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import moment from "moment-timezone";
import { useGuardedNavigate } from "../../context/NavigationGuardContext";
import { useNotifications } from "../../context/NotificationContext";
import { useAutoMarkRead } from "../../hooks/useAutoMarkRead";
import { useSalon } from "../../context/SalonContext";
import { DESIGN_ICONS } from "../../theme/designIcons";
import type { Notification } from "../../types/notifications";
import { formatDateTime } from "../../utils/timeUtils";
import { RELEASE_NOTE_EVENT } from "../../utils/releaseNotes";
import { getNotificationIcon } from "./notificationIcons";
import { getNotificationMessage } from "./notificationMessage";
import { getNotificationTarget } from "./notificationTarget";
import styles from "./Alerts.module.css";

/**
 * Notification colors arrive as Mantine names from the backend (body.color)
 * and notificationIcons (blue / green / orange …). Map them onto the brand
 * ramps so alert tiles stay on-palette (docs/DESIGN_SYSTEM.md §6).
 */
const BRAND_TONE: Record<string, string> = {
  blue: "teal",
  cyan: "teal",
  teal: "teal",
  green: "limeGreen",
  lime: "limeGreen",
  orange: "burntOrange",
  yellow: "burntOrange",
  red: "red",
  gray: "gray",
};
const toneOf = (color?: string) => BRAND_TONE[color ?? ""] ?? "teal";

/**
 * Events that scrolling never marks read. A release note clears only through
 * its toast's Refresh (mark read + reload); reading it from the list would
 * hide that toast before the tab picked up the new release.
 */
export const AUTO_READ_EXCLUDED_EVENTS: ReadonlySet<string> = new Set([
  RELEASE_NOTE_EVENT,
]);

/** The notifications API accepts at most this many read updates per call. */
const READ_BATCH_LIMIT = 100;

/** "Today" / "Yesterday" / "March 3, 2026", in the salon's timezone. */
function dayLabel(createdDt: string | undefined, timezone: string): string {
  const date = createdDt
    ? moment.utc(createdDt).tz(timezone)
    : moment().tz(timezone);
  const today = moment().tz(timezone);
  if (date.isSame(today, "day")) return "Today";
  if (date.isSame(today.clone().subtract(1, "day"), "day")) return "Yesterday";
  return date.format("MMMM D, YYYY");
}

function groupByDay(notifications: Notification[], timezone: string) {
  const groups = new Map<string, Notification[]>();
  for (const notification of notifications) {
    const key = dayLabel(notification.created_dt, timezone);
    groups.set(key, [...(groups.get(key) ?? []), notification]);
  }
  return Array.from(groups.entries());
}

interface AlertsListProps {
  /** Called after an alert opens its target (closes the popover / sheet). */
  onNavigate?: () => void;
}

/**
 * The scrollable, day-grouped list of alerts with infinite scroll. Each row
 * opens its target (appointment, client profile, or a release note's full
 * notes in a new tab) when it has one, and its
 * trailing ⋮ menu marks it read / unread (a teal dot marks unread rows). Shared by the desktop Alerts popover
 * and the mobile Alerts sheet.
 */
export const AlertsList: React.FC<AlertsListProps> = ({ onNavigate }) => {
  const {
    allNotifications,
    isLoading,
    error,
    hasMore,
    loadMoreNotifications,
    markNotificationAsRead,
    markMultipleNotificationsAsRead,
    markNotificationAsUnread,
  } = useNotifications();
  const { businessInfo } = useSalon() as {
    businessInfo: { timezone?: string } | null;
  };
  const timezone = businessInfo?.timezone || "UTC";
  // Guarded: opening an alert's target leaves the current page, so unsaved
  // changes (e.g. a Reports view) get the Save / Discard prompt first.
  const navigate = useGuardedNavigate();
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Rows read by scrolling keep their unread styling until the list unmounts
  // (the popover / sheet closes), so it's still clear what was new.
  const [seenThisOpen, setSeenThisOpen] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  // Rows the user marked unread themselves are never auto-read again while
  // the list is open (they'd flip straight back).
  const keptUnread = useRef(new Set<string>());

  const handleSeen = useCallback(
    (ids: string[]) => {
      setSeenThisOpen((prev) => new Set([...Array.from(prev), ...ids]));
      for (let i = 0; i < ids.length; i += READ_BATCH_LIMIT) {
        markMultipleNotificationsAsRead(ids.slice(i, i + READ_BATCH_LIMIT));
      }
    },
    [markMultipleNotificationsAsRead],
  );
  const autoReadRef = useAutoMarkRead({ onRead: handleSeen });

  const clearSeen = useCallback((notificationId: string) => {
    setSeenThisOpen((prev) => {
      if (!prev.has(notificationId)) return prev;
      const next = new Set(prev);
      next.delete(notificationId);
      return next;
    });
  }, []);

  // Load the next page when the bottom of the list scrolls into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoading) loadMoreNotifications();
      },
      { threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, loadMoreNotifications]);

  const groups = useMemo(
    () => groupByDay(allNotifications, timezone),
    [allNotifications, timezone],
  );

  const formatTime = (timestamp: string) => {
    if (!timestamp) return "";
    try {
      return formatDateTime(timestamp, timezone, "h:mm A");
    } catch {
      return moment.utc(timestamp).format("h:mm A");
    }
  };

  if (error) {
    return (
      <Alert color="red" title="Couldn't load alerts" m="sm">
        {error}
      </Alert>
    );
  }

  if (allNotifications.length === 0) {
    return isLoading ? (
      <Center py="xl">
        <Loader size="sm" />
      </Center>
    ) : (
      <Stack align="center" gap={6} py={40} px="md" ta="center">
        <ThemeIcon variant="light" color="teal" size={40} radius="md">
          <DESIGN_ICONS.Bell size={20} />
        </ThemeIcon>
        <Text fw={600} size="sm">
          You're all caught up
        </Text>
        <Text size="xs" c="dimmed">
          New alerts will show up here.
        </Text>
      </Stack>
    );
  }

  return (
    <Box className={styles.list}>
      {groups.map(([day, notifications]) => (
        <Box key={day} component="section" aria-label={day}>
          <Text className={styles.groupLabel}>{day}</Text>
          <Stack gap={2}>
            {notifications.map((notification) => {
              const id = notification.notification_id;
              const autoReadable =
                !notification.is_read &&
                !AUTO_READ_EXCLUDED_EVENTS.has(notification.event_name) &&
                !keptUnread.current.has(id);
              return (
                <AlertRow
                  key={id}
                  notification={notification}
                  showUnread={!notification.is_read || seenThisOpen.has(id)}
                  rowRef={autoReadRef(autoReadable ? id : null)}
                  time={formatTime(notification.created_dt)}
                  onOpen={(target) => {
                    if (!notification.is_read) {
                      markNotificationAsRead(notification.notification_id);
                    }
                    if (target.external) {
                      window.open(target.path, "_blank", "noopener,noreferrer");
                    } else {
                      navigate(target.path, { state: target.state });
                    }
                    onNavigate?.();
                  }}
                  onToggleRead={() => {
                    clearSeen(id);
                    if (notification.is_read) {
                      keptUnread.current.add(id);
                      markNotificationAsUnread(id);
                    } else {
                      markNotificationAsRead(id);
                    }
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      ))}
      <Box ref={sentinelRef} className={styles.sentinel}>
        {isLoading && hasMore && <Loader size="xs" />}
      </Box>
    </Box>
  );
};

interface AlertRowProps {
  notification: Notification;
  /** Unread styling (dot, bold); stays on for rows read by scrolling. */
  showUnread: boolean;
  /** Watches the row for auto-read; a no-op for rows that shouldn't be. */
  rowRef: (element: HTMLElement | null) => void;
  time: string;
  onOpen: (
    target: NonNullable<ReturnType<typeof getNotificationTarget>>,
  ) => void;
  onToggleRead: () => void;
}

const AlertRow: React.FC<AlertRowProps> = ({
  notification,
  showUnread,
  rowRef,
  time,
  onOpen,
  onToggleRead,
}) => {
  const { icon: EventIcon, defaultColor } = getNotificationIcon(
    notification.event_name,
    notification.body,
  );
  const message = getNotificationMessage(notification);
  const target = getNotificationTarget(notification);
  const isRead = notification.is_read;

  const content = (
    <>
      <ThemeIcon
        variant="light"
        color={toneOf(notification.body?.color || defaultColor)}
        size={32}
        radius={9}
        className={styles.tile}
      >
        <EventIcon size={16} />
      </ThemeIcon>
      <Box className={styles.text}>
        <Text className={styles.message} data-unread={showUnread || undefined}>
          {message}
        </Text>
        <Text className={styles.time}>{time}</Text>
      </Box>
    </>
  );

  return (
    <Box
      ref={rowRef}
      className={styles.row}
      data-unread={showUnread || undefined}
    >
      {target ? (
        <UnstyledButton
          className={styles.rowMain}
          aria-label={`${target.label}: ${message}`}
          onClick={() => onOpen(target)}
        >
          {content}
        </UnstyledButton>
      ) : (
        <Box className={styles.rowMain}>{content}</Box>
      )}
      <Box className={styles.rowActions}>
        {showUnread && (
          <Box
            component="span"
            className={styles.dot}
            role="img"
            aria-label="Unread"
          />
        )}
        {/* In the row's DOM (not portaled) so a press inside the menu stays
            inside the Alerts popover; fixed positioning keeps it from being
            clipped by the scrolling list. */}
        <Menu
          position="bottom-end"
          offset={4}
          withinPortal={false}
          floatingStrategy="fixed"
          classNames={{ dropdown: styles.menu, item: styles.menuItem }}
        >
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size={30}
              radius="md"
              className={styles.menuButton}
              aria-label="Alert options"
            >
              <DESIGN_ICONS.EllipsisVertical size={17} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={
                !isRead ? (
                  <DESIGN_ICONS.MailOpen size={15} />
                ) : (
                  <DESIGN_ICONS.Mail size={15} />
                )
              }
              onClick={onToggleRead}
            >
              {isRead ? "Mark as unread" : "Mark as read"}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Box>
    </Box>
  );
};
